<script>
import axios from 'axios';
import { signOut } from '@/helpers/system-guard';
import { systemState, STATUS_CACHE_KEY } from '@/helpers/system-status';
import SystemBannerList from '@/components/system-banner-list.vue';
import { applyTheme, themeFromStore, savedUserTheme, saveUserTheme } from '@/helpers/theme';

// The status is shared by every page (each page mounts its own layout): read at most
// this often, and kept in sessionStorage between pages.
const REFRESH_MS = 60 * 1000;
const ACTIVITY_EVENTS = ['mousemove', 'mousedown', 'keydown', 'scroll', 'touchstart', 'wheel'];

/**
 * System Configuration on every page (mounted by the layout): keeps the status
 * current (GET /system-config/status), signs a non-administrator out when
 * maintenance turns on, and runs the idle sign-out with its warning.
 *
 * The announcements show under the page title (PageHeader renders the list); on a
 * page without a PageHeader the list shows here, at the top.
 */
export default {
  name: 'SystemBanner',
  components: { SystemBannerList },
  data() {
    return {
      timer: null,
      idleTimer: null,
      lastActivity: Date.now(),
      // Seconds left before the idle sign-out, while the warning shows (null: hidden).
      idleSecondsLeft: null,
      // While the theme is being applied (its store changes aren't the person's own).
      applyingTheme: false,
    };
  },
  computed: {
    idleMinutes() {
      return Number(systemState.status?.idle_timeout_minutes) || 0;
    },
    // No PageHeader on this page: the list shows here instead.
    showListHere() {
      return systemState.headers === 0;
    },
    systemState() {
      return systemState;
    },
  },
  watch: {
    idleMinutes() {
      this.startIdleWatch();
    },
    // A new status (refresh, another OU): its theme.
    'systemState.status'() {
      this.applyAppearance();
    },
    // The person changed the theme with the gear: kept as their own.
    '$store.state.layout': {
      deep: true,
      handler() {
        if (!this.applyingTheme && !systemState.previewingTheme && systemState.allowUserTheme) saveUserTheme(themeFromStore(this.$store));
      },
    },
  },
  mounted() {
    if (!localStorage.getItem('jwt')) return;
    systemState.status = this.cached() || systemState.status;
    this.refresh(!systemState.status);
    this.timer = setInterval(() => {
      systemState.now = Date.now();
      this.refresh();
    }, 15 * 1000);
    ACTIVITY_EVENTS.forEach(e => window.addEventListener(e, this.onActivity, { passive: true }));
    this.startIdleWatch();
    // After the layout's own mounted() (which sets its default sidebar size and topbar).
    setTimeout(() => this.applyAppearance(), 0);
  },
  beforeUnmount() {
    clearInterval(this.timer);
    clearInterval(this.idleTimer);
    ACTIVITY_EVENTS.forEach(e => window.removeEventListener(e, this.onActivity));
  },
  methods: {
    ou() {
      return this.$store?.state?.activeOU || localStorage.getItem('activeOU') || '';
    },
    cached() {
      try {
        const c = JSON.parse(sessionStorage.getItem(STATUS_CACHE_KEY) || 'null');
        // Announcements can be limited to OUs: a status read in another OU doesn't count.
        return c && c.ou === this.ou() && Date.now() - c.at < REFRESH_MS ? c.data : null;
      } catch (e) {
        return null;
      }
    },
    async refresh(force = false) {
      if (!force && this.cached()) return;
      try {
        const { data } = await axios.get(`${process.env.VUE_APP_AUTHDOMAIN}/api/v1/system-config/status`, { params: { ou_code: this.ou() } });
        if (data?.status) {
          // Maintenance turned on: a non-administrator is signed out even on an idle page.
          if (data.data?.maintenance?.enabled && !data.data?.is_admin) {
            signOut({ type: 'maintenance', message: data.data.maintenance.message });
            return;
          }
          systemState.status = data.data;
          try { sessionStorage.setItem(STATUS_CACHE_KEY, JSON.stringify({ at: Date.now(), ou: this.ou(), data: data.data })); } catch (e) { /* not cached */ }
        }
      } catch (e) {
        // Maintenance / session replaced are handled by the system guard; anything else: try later.
      }
    },
    onActivity() {
      // While the warning shows, only "Stay signed in" keeps the session (a stray mouse move doesn't).
      if (this.idleSecondsLeft !== null) return;
      this.lastActivity = Date.now();
      try { localStorage.setItem('lastActivity', String(this.lastActivity)); } catch (e) { /* this tab only */ }
    },
    /**
     * Signs out after the configured minutes without activity (in any tab of this
     * browser), with a warning and a "Stay signed in" button for the last minute.
     */
    startIdleWatch() {
      clearInterval(this.idleTimer);
      this.idleSecondsLeft = null;
      if (!this.idleMinutes) return;
      const limit = this.idleMinutes * 60 * 1000;
      const warnFor = Math.min(60 * 1000, limit / 2);
      this.idleTimer = setInterval(() => {
        const shared = Number(localStorage.getItem('lastActivity')) || 0;
        const last = Math.max(this.lastActivity, shared);
        const left = limit - (Date.now() - last);
        if (left <= 0) {
          clearInterval(this.idleTimer);
          signOut({ type: 'idle', message: `You were signed out after ${this.idleMinutes} minute(s) without activity.` });
        } else if (left <= warnFor) {
          this.idleSecondsLeft = Math.ceil(left / 1000);
        } else if (this.idleSecondsLeft !== null) {
          this.idleSecondsLeft = null; // activity in another tab
        }
      }, 1000);
    },
    /**
     * The theme (System Configuration > Appearance): the person's own when they may
     * set one and have, else their OU's (or the global) theme once one is saved; with
     * none saved, the template's own look is left as it is.
     */
    applyAppearance() {
      const a = systemState.status?.appearance;
      if (!a || systemState.previewingTheme) return;
      systemState.allowUserTheme = !!a.allow_user_custom;
      document.documentElement.setAttribute('data-user-theme', a.allow_user_custom ? 'on' : 'off');

      const own = a.allow_user_custom ? savedUserTheme() : null;
      const theme = own || (a.configured ? a.theme : null);
      if (!theme) return;
      this.applyingTheme = true;
      applyTheme(theme, this.$store);
      this.$nextTick(() => { this.applyingTheme = false; });
    },
    staySignedIn() {
      this.idleSecondsLeft = null;
      this.onActivity();
    },
  },
};
</script>

<template>
  <!-- Idle: about to be signed out. -->
  <div v-if="idleSecondsLeft !== null" class="idle-warning shadow-lg" role="alertdialog" aria-live="assertive">
    <div class="d-flex align-items-start gap-2">
      <i class="ri-timer-line fs-20 text-warning"></i>
      <div>
        <div class="fw-semibold">You'll be signed out in {{ idleSecondsLeft }} second{{ idleSecondsLeft === 1 ? '' : 's' }}</div>
        <div class="small text-muted">There's been no activity for a while.</div>
        <b-button size="sm" variant="primary" class="mt-2" @click="staySignedIn">Stay signed in</b-button>
      </div>
    </div>
  </div>

  <!-- Pages without a PageHeader: the announcements here (else under the page title). -->
  <SystemBannerList v-if="showListHere" />
</template>

<style>
/* People may not set their own theme: no Theme Customizer gear. */
html[data-user-theme="off"] .customizer-setting { display: none !important; }
/* The sidebar image (Appearance): the template draws it at 7%, too faint to notice. */
html[data-sidebar-image]:not([data-sidebar-image="none"]) .sidebar-background { opacity: .18; }
</style>

<style scoped>
.idle-warning {
  position: fixed;
  right: 1.25rem;
  bottom: 1.25rem;
  z-index: 1090;
  max-width: 320px;
  background: var(--vz-card-bg, #fff);
  border: 1px solid var(--vz-border-color, #e9ebec);
  border-radius: 0.5rem;
  padding: 0.9rem 1rem;
}
</style>
